import { useState, useEffect, useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  Server,
  Layers,
  Boxes,
  HardDrive,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MoreVertical,
} from "lucide-react";
import { router } from "@inertiajs/react";
import { useToast } from "../ui/toast";
import DismissRecommendationModal from "./DismissRecommendationModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { RecommendationSection, Recommendation } from "@/types/recommendation";

const SECTION_ICONS = {
  server: Server,
  layers: Layers,
  boxes: Boxes,
  "hard-drive": HardDrive,
} as const;

const SECTION_NOUNS: Record<string, string> = {
  ec2: "instances",
  asg: "auto scaling groups",
  fargate: "Fargate services",
  ebs: "EBS volumes",
};

type FieldKey = "cpu" | "memory" | "scale" | "size";

const FIELD_ORDER: FieldKey[] = ["cpu", "memory", "scale", "size"];

function columnHeader(
  field: FieldKey,
  sectionKey: string
): { label: string; title?: string; width: string } {
  switch (field) {
    case "cpu":
      return { label: "CPU", title: "P99.5 (primary) · avg (secondary)", width: "w-28" };
    case "memory":
      return { label: "Memory", title: "P99.5 (primary) · avg (secondary)", width: "w-28" };
    case "scale":
      return sectionKey === "fargate"
        ? { label: "Tasks", title: "Desired task count", width: "w-16" }
        : { label: "Scale", title: "Desired / max capacity", width: "w-16" };
    case "size":
      return { label: "Size", width: "w-20" };
  }
}

function formatInstanceType(value: string, resourceType: string): string {
  if (resourceType === "Aws::Ecs::FargateService") {
    const [cpuUnits, memoryMB] = value.split(":").map(Number);
    const vcpu = cpuUnits / 1024;
    const gb = memoryMB / 1024;
    return `${vcpu}vCPU-${gb}GB`;
  }
  return value;
}

interface RecommendationTableRowProps {
  recommendation: Recommendation;
  workspaceSlug: string;
  organizationPrsRemaining: number;
  organizationPlan: string;
  columns: FieldKey[];
}

function RecommendationTableRow({
  recommendation,
  workspaceSlug,
  organizationPrsRemaining,
  organizationPlan,
  columns,
}: RecommendationTableRowProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);
  const { showToast } = useToast();

  const handleGeneratePR = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (organizationPrsRemaining === 0) {
      router.visit("/pricing");
      return;
    }

    setIsGenerating(true);

    try {
      const response = await fetch(
        `/workspaces/${workspaceSlug}/recommendations/${recommendation.uuid}/generate_pr`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        const message = result.message || "PR generation initiated successfully";
        const prsRemainingMsg =
          organizationPlan === "free" && result.prs_remaining !== undefined
            ? ` ${result.prs_remaining} free ${result.prs_remaining === 1 ? "PR" : "PRs"} remaining.`
            : "";
        showToast("success", `${message}.${prsRemainingMsg}`);
        router.reload({ only: ["recommendation_sections", "recommendation_summary"] });
      } else {
        const errorMessage = result.errors
          ? Array.isArray(result.errors)
            ? result.errors.join(", ")
            : result.errors
          : "Failed to generate PR";
        showToast("error", errorMessage);
      }
    } catch {
      showToast("error", "Failed to generate PR");
    } finally {
      setIsGenerating(false);
    }
  };

  const memoryMetric = recommendation.metrics.memory;
  const cpuMetric = recommendation.metrics.cpu;

  return (
    <>
      <tr className="border-b last:border-0 transition-colors hover:bg-surface/30">
        {/* Resource */}
        <td className="px-4 py-3">
          <span className="font-mono text-xs text-foreground block whitespace-nowrap">
            {recommendation.display_name}
          </span>
          <span className="text-xs text-surface-foreground/40 mt-0.5 block whitespace-nowrap">
            {recommendation.region}
            {recommendation.display_fields.includes("cpu") &&
              cpuMetric?.period_days != null &&
              ` · ${cpuMetric.period_days} days`}
          </span>
        </td>

        {columns.map((col) => {
          const hasField = recommendation.display_fields.includes(col);
          if (!hasField) {
            return (
              <td key={col} className="px-2 py-3 text-center align-middle">
                <span className="text-xs text-surface-foreground/40">-</span>
              </td>
            );
          }
          if (col === "cpu" || col === "memory") {
            const metric = col === "cpu" ? cpuMetric : memoryMetric;
            return (
              <td key={col} className="px-2 py-3 text-center">
                {metric ? (
                  <>
                    <span className="font-mono text-xs text-foreground block">
                      {metric.p995}%
                    </span>
                    <span className="font-mono text-xs text-surface-foreground/40 block">
                      avg {metric.avg}%
                    </span>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap">
                    <AlertTriangle className="size-3 shrink-0" />
                    No data
                  </span>
                )}
              </td>
            );
          }
          const value = col === "size" ? recommendation.size : recommendation.scale;
          return (
            <td key={col} className="px-2 py-3 text-center align-middle">
              {value ? (
                <span className="font-mono text-xs text-foreground">{value}</span>
              ) : (
                <span className="text-xs text-surface-foreground/40">-</span>
              )}
            </td>
          );
        })}

        {/* Current */}
        <td className="px-2 py-3 text-center align-middle">
          <span className="font-mono text-xs text-foreground">
            {formatInstanceType(
              recommendation.from_instance_type,
              recommendation.resource_type
            )}
          </span>
        </td>

        {/* Recommended */}
        <td className="px-2 py-3 text-center align-middle">
          <span className="font-mono text-xs text-primary font-medium">
            {formatInstanceType(
              recommendation.to_instance_type,
              recommendation.resource_type
            )}
          </span>
        </td>

        {/* Savings */}
        <td className="px-2 py-3 text-center align-middle">
          <span className="text-xs text-green-600 dark:text-green-400 font-semibold">
            ${recommendation.estimated_monthly_savings.toFixed(2)}/mo
          </span>
        </td>

        {/* Actions */}
        <td className="px-2 py-3 text-center">
          {recommendation.status === "pending" ? (
            <div className="inline-flex items-center gap-1">
              {recommendation.terraform_info?.status === "not_found" ? (
                <button
                  className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap bg-surface text-surface-foreground/50 cursor-not-allowed"
                  disabled
                  title="This resource was not found in your Terraform state files"
                >
                  Not in Terraform
                </button>
              ) : recommendation.terraform_info?.status === "pending" ? (
                <button
                  className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap bg-surface text-surface-foreground/50 cursor-not-allowed inline-flex items-center gap-1"
                  disabled
                >
                  <Loader2 className="size-3 animate-spin" />
                  Checking...
                </button>
              ) : (
              <button
                className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={handleGeneratePR}
                disabled={!recommendation.actionable_via_pr || isGenerating}
              >
                {isGenerating ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="size-3 animate-spin" />
                    Generating...
                  </span>
                ) : organizationPrsRemaining === 0 ? (
                  "Subscribe"
                ) : (
                  "Generate PR"
                )}
              </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 rounded-md text-surface-foreground/50 hover:text-foreground hover:bg-surface/50 transition-colors"
                    aria-label="More options"
                  >
                    <MoreVertical className="size-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => setIsDismissModalOpen(true)}
                  >
                    Dismiss
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : recommendation.status === "implementing" ? (
            <>
              {recommendation.terraform_implementation?.status === "pending" ? (
                <button
                  className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap bg-primary text-primary-foreground opacity-50 cursor-not-allowed inline-flex items-center gap-1"
                  disabled
                >
                  <Loader2 className="size-3 animate-spin" />
                  Generating...
                </button>
              ) : recommendation.terraform_implementation?.status === "pr_created" &&
                recommendation.terraform_implementation.pr_url ? (
                <a
                  href={recommendation.terraform_implementation.pr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap bg-primary text-primary-foreground hover:bg-primary/90 transition-colors inline-flex items-center"
                >
                  View PR
                </a>
              ) : null}
            </>
          ) : null}
        </td>
      </tr>

      <DismissRecommendationModal
        isOpen={isDismissModalOpen}
        onClose={() => setIsDismissModalOpen(false)}
        workspaceSlug={workspaceSlug}
        recommendationUuid={recommendation.uuid}
      />
    </>
  );
}

interface RecommendationTableProps {
  section: RecommendationSection;
  workspaceSlug: string;
  organizationPrsRemaining: number;
  organizationPlan: string;
}

export function RecommendationTable({
  section,
  workspaceSlug,
  organizationPrsRemaining,
  organizationPlan,
}: RecommendationTableProps) {
  const [updatedAgo, setUpdatedAgo] = useState<string | null>(null);

  useEffect(() => {
    if (section.last_updated_at) {
      setUpdatedAgo(
        formatDistanceToNow(new Date(section.last_updated_at), {
          addSuffix: true,
        })
      );
    }
  }, [section.last_updated_at]);

  const Icon = SECTION_ICONS[section.icon];
  const noun = SECTION_NOUNS[section.key] ?? "resources";
  const columns = useMemo(() => {
    const present = new Set<FieldKey>(
      section.recommendations.flatMap((r) => r.display_fields)
    );
    return FIELD_ORDER.filter((f) => present.has(f));
  }, [section.recommendations]);

  const footerLeft =
    section.instances_analyzed != null
      ? `Analyzed ${section.instances_analyzed} ${noun} across ${section.regions_count} ${section.regions_count === 1 ? "region" : "regions"}`
      : `${section.count} ${section.count === 1 ? "recommendation" : "recommendations"} across ${section.regions_count} ${section.regions_count === 1 ? "region" : "regions"}`;

  return (
    <div className="rounded-2xl border bg-background/80 shadow-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 min-h-12 border-b bg-background/50">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-primary" />
          <span className="font-semibold text-sm">{section.label}</span>
        </div>
        <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">
          {section.count} found
        </span>
      </div>

      {/* Table or empty state */}
      {section.count === 0 ? (
        <div className="px-4 py-6 flex items-center justify-center gap-2 text-surface-foreground/40">
          <CheckCircle2 className="size-3.5 shrink-0" />
          <span className="text-xs">All caught up</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b bg-surface/30 text-xs text-surface-foreground/50 font-medium">
                <th className="text-left px-4 py-2">Resource</th>
                {columns.map((col) => {
                  const header = columnHeader(col, section.key);
                  return (
                    <th
                      key={col}
                      className={`text-center px-2 py-2 ${header.width}`}
                      title={header.title}
                    >
                      {header.label}
                    </th>
                  );
                })}
                <th className="text-center px-2 py-2 w-20">Current</th>
                <th className="text-center px-2 py-2 w-24">Recommended</th>
                <th className="text-center px-2 py-2 w-20">Savings</th>
                <th className="w-32 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {section.recommendations.map((rec) => (
                <RecommendationTableRow
                  key={rec.id}
                  recommendation={rec}
                  workspaceSlug={workspaceSlug}
                  organizationPrsRemaining={organizationPrsRemaining}
                  organizationPlan={organizationPlan}
                  columns={columns}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-2.5 bg-surface/20 border-t text-xs text-surface-foreground/60 flex justify-between">
        <span>{footerLeft}</span>
        {updatedAgo && <span>Updated {updatedAgo}</span>}
      </div>
    </div>
  );
}
